// sprites.ts — the game's own board sprites, copied from Assets/Sprites by tools/sync_editor_sprites.py
// and bundled by webpack, so the editor board draws with exactly the art the game does. Loaded once, up
// front; until an image has decoded, the draw code falls back to the procedural art in cellArt.ts, and
// every listener registered with onSpritesLoaded redraws when the set is complete.
//
// ⚠ Positions and sizes are the GAME's: every sprite is 100 px per world unit (its import setting), and a
// board cell is 1.485 world units (MarbleGrid's cell size in the gameplay scene). So one cell is
// CELL_PX = 148.5 sprite pixels, and a sprite drawn into a cell of S canvas pixels scales by S / CELL_PX.
import { MarbleColor } from "../Models/LevelData";
import boxClosedRed01 from "./sprites/box_closed_red01.png";
import boxClosedGreen01 from "./sprites/box_closed_green01.png";
import boxClosedBlue01 from "./sprites/box_closed_blue01.png";
import boxClosedBlue02 from "./sprites/box_closed_blue02.png";
import boxClosedYellow01 from "./sprites/box_closed_yellow01.png";
import boxClosedPurple from "./sprites/box_closed_purple.png";
import boxClosedPink from "./sprites/box_closed_pink.png";
import boxClosedGreen02 from "./sprites/box_closed_green02.png";
import boxClosedGreen03 from "./sprites/box_closed_green03.png";
import boxClosedOrange from "./sprites/box_closed_orange.png";
import boxClosedBrown from "./sprites/box_closed_brown.png";
import boxClosedBlack from "./sprites/box_closed_black.png";
import boxClosedWhite from "./sprites/box_closed_white.png";
import boxClosedNeutral from "./sprites/box_closed_neutral.png";
import pitRed01 from "./sprites/box_closed_pit_red01.png";
import pitGreen01 from "./sprites/box_closed_pit_green01.png";
import pitBlue01 from "./sprites/box_closed_pit_blue01.png";
import pitBlue02 from "./sprites/box_closed_pit_blue02.png";
import pitYellow01 from "./sprites/box_closed_pit_yellow01.png";
import pitPurple from "./sprites/box_closed_pit_purple.png";
import pitPink from "./sprites/box_closed_pit_pink.png";
import pitGreen02 from "./sprites/box_closed_pit_green02.png";
import pitGreen03 from "./sprites/box_closed_pit_green03.png";
import pitOrange from "./sprites/box_closed_pit_orange.png";
import pitBrown from "./sprites/box_closed_pit_brown.png";
import pitBlack from "./sprites/box_closed_pit_black.png";
import pitWhite from "./sprites/box_closed_pit_white.png";
import pitNeutral from "./sprites/box_closed_pit_neutral.png";
import arrowRed01 from "./sprites/arrow_red01.png";
import arrowGreen01 from "./sprites/arrow_green01.png";
import arrowBlue01 from "./sprites/arrow_blue01.png";
import arrowBlue02 from "./sprites/arrow_blue02.png";
import arrowYellow01 from "./sprites/arrow_yellow01.png";
import arrowPurple from "./sprites/arrow_purple.png";
import arrowPink from "./sprites/arrow_pink.png";
import arrowGreen02 from "./sprites/arrow_green02.png";
import arrowGreen03 from "./sprites/arrow_green03.png";
import arrowOrange from "./sprites/arrow_orange.png";
import arrowBrown from "./sprites/arrow_brown.png";
import arrowBlack from "./sprites/arrow_black.png";
import arrowWhite from "./sprites/arrow_white.png";
import arrowNeutral from "./sprites/arrow_neutral.png";
// The closed double box (Box.Double): its frame, drawn over the closed box standing inside it, and the groove line
// under the frame (one sprite for every colour).
import boxDoubleFrameRed01 from "./sprites/box_double_frame_red01.png";
import boxDoubleFrameGreen01 from "./sprites/box_double_frame_green01.png";
import boxDoubleFrameBlue01 from "./sprites/box_double_frame_blue01.png";
import boxDoubleFrameBlue02 from "./sprites/box_double_frame_blue02.png";
import boxDoubleFrameYellow01 from "./sprites/box_double_frame_yellow01.png";
import boxDoubleFramePurple from "./sprites/box_double_frame_purple.png";
import boxDoubleFramePink from "./sprites/box_double_frame_pink.png";
import boxDoubleFrameGreen02 from "./sprites/box_double_frame_green02.png";
import boxDoubleFrameGreen03 from "./sprites/box_double_frame_green03.png";
import boxDoubleFrameOrange from "./sprites/box_double_frame_orange.png";
import boxDoubleFrameBrown from "./sprites/box_double_frame_brown.png";
import boxDoubleFrameBlack from "./sprites/box_double_frame_black.png";
import boxDoubleFrameWhite from "./sprites/box_double_frame_white.png";
import boxDoubleFrameNeutral from "./sprites/box_double_frame_neutral.png";
import boxDoubleGroove from "./sprites/box_double_groove.png";
import jackBodyRed01 from "./sprites/jumping_jack_body_red01.png";
import jackBodyGreen01 from "./sprites/jumping_jack_body_green01.png";
import jackBodyBlue01 from "./sprites/jumping_jack_body_blue01.png";
import jackBodyBlue02 from "./sprites/jumping_jack_body_blue02.png";
import jackBodyYellow01 from "./sprites/jumping_jack_body_yellow01.png";
import jackBodyPurple from "./sprites/jumping_jack_body_purple.png";
import jackBodyPink from "./sprites/jumping_jack_body_pink.png";
import jackBodyGreen02 from "./sprites/jumping_jack_body_green02.png";
import jackBodyGreen03 from "./sprites/jumping_jack_body_green03.png";
import jackBodyOrange from "./sprites/jumping_jack_body_orange.png";
import jackBodyBrown from "./sprites/jumping_jack_body_brown.png";
import jackBodyBlack from "./sprites/jumping_jack_body_black.png";
import jackBodyWhite from "./sprites/jumping_jack_body_white.png";
import jackBodyNeutral from "./sprites/jumping_jack_body_neutral.png";
import jackCapRed01 from "./sprites/jumping_jack_closed_cap_red01.png";
import jackCapGreen01 from "./sprites/jumping_jack_closed_cap_green01.png";
import jackCapBlue01 from "./sprites/jumping_jack_closed_cap_blue01.png";
import jackCapBlue02 from "./sprites/jumping_jack_closed_cap_blue02.png";
import jackCapYellow01 from "./sprites/jumping_jack_closed_cap_yellow01.png";
import jackCapPurple from "./sprites/jumping_jack_closed_cap_purple.png";
import jackCapPink from "./sprites/jumping_jack_closed_cap_pink.png";
import jackCapGreen02 from "./sprites/jumping_jack_closed_cap_green02.png";
import jackCapGreen03 from "./sprites/jumping_jack_closed_cap_green03.png";
import jackCapOrange from "./sprites/jumping_jack_closed_cap_orange.png";
import jackCapBrown from "./sprites/jumping_jack_closed_cap_brown.png";
import jackCapBlack from "./sprites/jumping_jack_closed_cap_black.png";
import jackCapWhite from "./sprites/jumping_jack_closed_cap_white.png";
import jackCapNeutral from "./sprites/jumping_jack_closed_cap_neutral.png";
import itemJumpingJackBodyPatch from "./sprites/JumpingJack_Body_Patch.png";
import itemJumpingJackButtonPatch from "./sprites/JumpingJack_Button_Patch.png";
import jackMysteryBody from "./sprites/JumpingJack_BodyMystery.png";
import jackMysteryCap from "./sprites/JumpingJack_ClosedMystery_Cap.png";
import jackMysteryButton from "./sprites/JumpingJack_ButtonMystery_Patch.png";
import boxMystery from "./sprites/box_mystery.png";
// The glide box's wing (one RIGHT wing, mirrored for the left) — drawn at 300 px per unit, not 100.
import boxGlideWing from "./sprites/box_glide_wing.png";
// The split box's lid as the game draws it: the medium-groove grey INDEX sprite (index in red, alpha in green,
// opaque so the canvas cannot round the index) and its 256 x 13 ramps. Drawn by art/splitLid.ts.
import boxSplitIndex from "./sprites/box_split_closed_index.png";
import boxSplitRamps from "./sprites/box_split_closed_ramps.png";
import emptySpace from "./sprites/EmptySpace.png";
import tunnel from "./sprites/tunnel.png";
import tunnelShadow from "./sprites/tunnel_shadow.png";
import gate from "./sprites/gate.png";
import connectorVertical from "./sprites/connector_vertical.png";
import connectorHorizontal from "./sprites/connector_horizontal.png";
import connectorBoxCover from "./sprites/connector_box_cover.png";
import metalPlate from "./sprites/metal_plate.png";
import metalPlateShadow from "./sprites/metal_plate_shadow.png";
// The game-mechanics items (copied as <Folder>_<file>.png; see tools/sync_editor_sprites.py).
import itemBoltEyeBoltBoltHorizontal from "./sprites/Bolt_EyeBolt_Bolt_Horizontal.png";
import itemBoltEyeBoltBoltVertical from "./sprites/Bolt_EyeBolt_Bolt_Vertical.png";
import itemBoltEyeBoltMiddleHorizontal from "./sprites/Bolt_EyeBolt_Middle_Horizontal.png";
import itemBoltEyeBoltMiddleVertical from "./sprites/Bolt_EyeBolt_Middle_Vertical.png";
import itemBoltEyeBoltPatchHorizontal from "./sprites/Bolt_EyeBolt_Patch_Horizontal.png";
import itemBoltEyeBoltPatchVertical from "./sprites/Bolt_EyeBolt_Patch_Vertical.png";
import itemElevatorBody from "./sprites/Elevator_Body.png";
import itemElevatorBodyPatch from "./sprites/Elevator_BodyPatch.png";
import itemElevatorDoorL from "./sprites/Elevator_DoorL.png";
import itemElevatorDoorR from "./sprites/Elevator_DoorR.png";
import itemFrozenBoxFrozenBox from "./sprites/FrozenBox_FrozenBox.png";
import itemFrozenBoxFrozenBoxBroken from "./sprites/FrozenBox_FrozenBoxBroken.png";
import itemLaserGateBlueKeyFrame001 from "./sprites/LaserGate_BlueKey_Frame001.png";
import itemLaserGateBlueLaserGate from "./sprites/LaserGate_BlueLaserGate.png";
import itemLaserGateBlueLaserLock from "./sprites/LaserGate_BlueLaserLock.png";
import itemLaserGateKeyFrame001 from "./sprites/LaserGate_Key_Frame001.png";
import itemLaserGateLaser from "./sprites/LaserGate_Laser.png";
import itemLaserGateLaserGate from "./sprites/LaserGate_LaserGate.png";
import itemLaserGateLaserLock from "./sprites/LaserGate_LaserLock.png";
import itemMetalBoxMetalBoxBody from "./sprites/MetalBox_MetalBox_Body.png";
import itemMetalBoxMetalBoxChain01 from "./sprites/MetalBox_MetalBox_Chain01.png";
import itemMetalBoxMetalBoxChain01Shadow from "./sprites/MetalBox_MetalBox_Chain01_Shadow.png";
import itemMetalBoxMetalBoxChain02 from "./sprites/MetalBox_MetalBox_Chain02.png";
import itemMetalBoxMetalBoxChain02Shadow from "./sprites/MetalBox_MetalBox_Chain02_Shadow.png";
import itemSlidingDoorBodyHorizontal from "./sprites/SlidingDoor_Body_Horizontal.png";
import itemSlidingDoorBodyVertical from "./sprites/SlidingDoor_Body_Vertical.png";
import itemSlidingDoorMiddleHorizontal from "./sprites/SlidingDoor_Middle_Horizontal.png";
import itemSlidingDoorMiddleVertical from "./sprites/SlidingDoor_Middle_Vertical.png";
import itemSlidingDoorTipHorizontal from "./sprites/SlidingDoor_Tip_Horizontal.png";
import itemSlidingDoorTipVertical from "./sprites/SlidingDoor_Tip_Vertical.png";
import itemSuperMysteryBoxHiddenScrewBoxBody from "./sprites/SuperMysteryBox_HiddenScrewBoxBody.png";
import itemSuperMysteryBoxHiddenScrewBoxCover01 from "./sprites/SuperMysteryBox_HiddenScrewBoxCover01.png";
import itemSuperMysteryBoxHiddenScrewBoxCover01Shadow from "./sprites/SuperMysteryBox_HiddenScrewBoxCover01Shadow.png";
import itemSuperTunnelTunnel from "./sprites/SuperTunnel_Tunnel.png";
import itemSuperTunnelTunnelPatch from "./sprites/SuperTunnel_TunnelPatch.png";
import itemSuperTunnelTunnelShadow from "./sprites/SuperTunnel_TunnelShadow.png";
import itemToolBoxToolBox from "./sprites/ToolBox_ToolBox.png";
// The pipe (TWGS.Gameplay Pipe): the kit art-gen/pipe-mesh.html renders and export-pipe-sprites.mjs ships to
// Assets/Sprites/Gameplay/Pipe — the same five files, renamed to this folder's Item_Part style.
import itemPipeHead from "./sprites/Pipe_Head.png";
import itemPipeHeadShadow from "./sprites/Pipe_HeadShadow.png";
import itemPipeBody from "./sprites/Pipe_Body.png";
import itemPipeBodyShadow from "./sprites/Pipe_BodyShadow.png";
import itemPipeWallEnd from "./sprites/Pipe_WallEnd.png";
/** Sprite pixels per board cell: 1.485 world units at the art's 100 px per unit. */
export const CELL_PX = 148.5;
/** The colourless set, for a box whose colour the seed has not dealt yet. */
export const NEUTRAL = "neutral";
const CLOSED = {
    [MarbleColor.Red01]: boxClosedRed01, [MarbleColor.Green01]: boxClosedGreen01,
    [MarbleColor.Blue01]: boxClosedBlue01, [MarbleColor.Blue02]: boxClosedBlue02,
    [MarbleColor.Yellow01]: boxClosedYellow01, [MarbleColor.Purple]: boxClosedPurple,
    [MarbleColor.Pink]: boxClosedPink, [MarbleColor.Green02]: boxClosedGreen02,
    [MarbleColor.Green03]: boxClosedGreen03, [MarbleColor.Orange]: boxClosedOrange,
    [MarbleColor.Brown]: boxClosedBrown, [MarbleColor.Black]: boxClosedBlack,
    [MarbleColor.White]: boxClosedWhite, [NEUTRAL]: boxClosedNeutral,
};
const PIT = {
    [MarbleColor.Red01]: pitRed01, [MarbleColor.Green01]: pitGreen01,
    [MarbleColor.Blue01]: pitBlue01, [MarbleColor.Blue02]: pitBlue02,
    [MarbleColor.Yellow01]: pitYellow01, [MarbleColor.Purple]: pitPurple,
    [MarbleColor.Pink]: pitPink, [MarbleColor.Green02]: pitGreen02,
    [MarbleColor.Green03]: pitGreen03, [MarbleColor.Orange]: pitOrange,
    [MarbleColor.Brown]: pitBrown, [MarbleColor.Black]: pitBlack,
    [MarbleColor.White]: pitWhite, [NEUTRAL]: pitNeutral,
};
const ARROW = {
    [MarbleColor.Red01]: arrowRed01, [MarbleColor.Green01]: arrowGreen01,
    [MarbleColor.Blue01]: arrowBlue01, [MarbleColor.Blue02]: arrowBlue02,
    [MarbleColor.Yellow01]: arrowYellow01, [MarbleColor.Purple]: arrowPurple,
    [MarbleColor.Pink]: arrowPink, [MarbleColor.Green02]: arrowGreen02,
    [MarbleColor.Green03]: arrowGreen03, [MarbleColor.Orange]: arrowOrange,
    [MarbleColor.Brown]: arrowBrown, [MarbleColor.Black]: arrowBlack,
    [MarbleColor.White]: arrowWhite, [NEUTRAL]: arrowNeutral,
};
const DOUBLE_FRAME = {
    [MarbleColor.Red01]: boxDoubleFrameRed01,
    [MarbleColor.Green01]: boxDoubleFrameGreen01,
    [MarbleColor.Blue01]: boxDoubleFrameBlue01,
    [MarbleColor.Blue02]: boxDoubleFrameBlue02,
    [MarbleColor.Yellow01]: boxDoubleFrameYellow01,
    [MarbleColor.Purple]: boxDoubleFramePurple,
    [MarbleColor.Pink]: boxDoubleFramePink,
    [MarbleColor.Green02]: boxDoubleFrameGreen02,
    [MarbleColor.Green03]: boxDoubleFrameGreen03,
    [MarbleColor.Orange]: boxDoubleFrameOrange,
    [MarbleColor.Brown]: boxDoubleFrameBrown,
    [MarbleColor.Black]: boxDoubleFrameBlack,
    [MarbleColor.White]: boxDoubleFrameWhite,
    [NEUTRAL]: boxDoubleFrameNeutral,
};
const JACK_BODY = {
    [MarbleColor.Red01]: jackBodyRed01,
    [MarbleColor.Green01]: jackBodyGreen01,
    [MarbleColor.Blue01]: jackBodyBlue01,
    [MarbleColor.Blue02]: jackBodyBlue02,
    [MarbleColor.Yellow01]: jackBodyYellow01,
    [MarbleColor.Purple]: jackBodyPurple,
    [MarbleColor.Pink]: jackBodyPink,
    [MarbleColor.Green02]: jackBodyGreen02,
    [MarbleColor.Green03]: jackBodyGreen03,
    [MarbleColor.Orange]: jackBodyOrange,
    [MarbleColor.Brown]: jackBodyBrown,
    [MarbleColor.Black]: jackBodyBlack,
    [MarbleColor.White]: jackBodyWhite,
    [NEUTRAL]: jackBodyNeutral,
};
const JACK_CAP = {
    [MarbleColor.Red01]: jackCapRed01,
    [MarbleColor.Green01]: jackCapGreen01,
    [MarbleColor.Blue01]: jackCapBlue01,
    [MarbleColor.Blue02]: jackCapBlue02,
    [MarbleColor.Yellow01]: jackCapYellow01,
    [MarbleColor.Purple]: jackCapPurple,
    [MarbleColor.Pink]: jackCapPink,
    [MarbleColor.Green02]: jackCapGreen02,
    [MarbleColor.Green03]: jackCapGreen03,
    [MarbleColor.Orange]: jackCapOrange,
    [MarbleColor.Brown]: jackCapBrown,
    [MarbleColor.Black]: jackCapBlack,
    [MarbleColor.White]: jackCapWhite,
    [NEUTRAL]: jackCapNeutral,
};
const SINGLES = {
    itemJumpingJackBodyPatch, itemJumpingJackButtonPatch, jackMysteryBody, jackMysteryCap, jackMysteryButton,
    boxMystery, boxDoubleGroove, boxGlideWing, boxSplitIndex, boxSplitRamps, emptySpace, tunnel, tunnelShadow, gate,
    connectorVertical, connectorHorizontal, connectorBoxCover, metalPlate, metalPlateShadow,
    itemBoltEyeBoltBoltHorizontal,
    itemBoltEyeBoltBoltVertical,
    itemBoltEyeBoltMiddleHorizontal,
    itemBoltEyeBoltMiddleVertical,
    itemBoltEyeBoltPatchHorizontal,
    itemBoltEyeBoltPatchVertical,
    itemFrozenBoxFrozenBox,
    itemFrozenBoxFrozenBoxBroken,
    itemLaserGateBlueKeyFrame001,
    itemLaserGateBlueLaserGate,
    itemLaserGateBlueLaserLock,
    itemLaserGateKeyFrame001,
    itemLaserGateLaser,
    itemLaserGateLaserGate,
    itemLaserGateLaserLock,
    itemMetalBoxMetalBoxBody,
    itemMetalBoxMetalBoxChain01,
    itemMetalBoxMetalBoxChain01Shadow,
    itemMetalBoxMetalBoxChain02,
    itemMetalBoxMetalBoxChain02Shadow,
    itemSlidingDoorBodyHorizontal,
    itemSlidingDoorBodyVertical,
    itemSlidingDoorMiddleHorizontal,
    itemSlidingDoorMiddleVertical,
    itemSlidingDoorTipHorizontal,
    itemSlidingDoorTipVertical,
    itemSuperMysteryBoxHiddenScrewBoxBody,
    itemSuperMysteryBoxHiddenScrewBoxCover01,
    itemSuperMysteryBoxHiddenScrewBoxCover01Shadow,
    itemSuperTunnelTunnel,
    itemSuperTunnelTunnelPatch,
    itemSuperTunnelTunnelShadow,
    itemToolBoxToolBox,
    itemPipeHead,
    itemPipeHeadShadow,
    itemPipeBody,
    itemPipeBodyShadow,
    itemPipeWallEnd,
    itemElevatorBody,
    itemElevatorBodyPatch,
    itemElevatorDoorL,
    itemElevatorDoorR,
};
// url -> decoded image. Only COMPLETE images are handed out, so a caller never draws a half-loaded one.
const images = new Map();
const listeners = new Set();
let pending = 0;
function load(url) {
    if (typeof Image === "undefined" || images.has(url)) {
        return; // no DOM (tests), or already asked for
    }
    const img = new Image();
    images.set(url, img);
    pending++;
    const done = () => {
        pending--;
        if (pending === 0) {
            for (const l of listeners)
                l();
        }
    };
    img.onload = done;
    img.onerror = done; // a missing file leaves the procedural fallback in place rather than stalling
    img.src = url;
}
for (const url of [...Object.values(CLOSED), ...Object.values(PIT), ...Object.values(ARROW), ...Object.values(DOUBLE_FRAME), ...Object.values(JACK_BODY), ...Object.values(JACK_CAP), ...Object.values(SINGLES)]) {
    load(url);
}
function ready(url) {
    if (!url)
        return null;
    const img = images.get(url);
    return img && img.complete && img.naturalWidth > 0 ? img : null;
}
/** Calls `redraw` once every sprite has settled (loaded or failed). Returns the unsubscribe. */
export function onSpritesLoaded(redraw) {
    listeners.add(redraw);
    return () => listeners.delete(redraw);
}
export const Sprites = {
    boxClosed: (tint) => ready(CLOSED[tint]),
    boxPit: (tint) => ready(PIT[tint]),
    arrow: (tint) => ready(ARROW[tint]),
    boxDoubleFrame: (tint) => ready(DOUBLE_FRAME[tint]),
    jackBody: (tint) => ready(JACK_BODY[tint]),
    jackClosedCap: (tint) => ready(JACK_CAP[tint]),
    single: (key) => ready(SINGLES[key]),
};
